import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  Loader2, Plus, Users, Download, Send, Copy, Archive, Edit2,
  ExternalLink, MoreHorizontal, Check, X, Calendar, MapPin,
  ChevronDown, ChevronUp, Save, Trash2, AlertTriangle, Sparkles
} from 'lucide-react';
import EmptyState from '../components/EmptyState';
import { toast } from 'sonner';
import ConfigDialog from '../components/ConfigDialog';
import { ProcessBar } from '../components/ProcessBar';

const TYPE_COLORS = {
  E1: { bg: '#DBEAFE', text: '#1D4ED8', label: 'Workshop' },
  E2: { bg: '#F3E8FF', text: '#7C3AED', label: 'Webinar' },
  E3: { bg: '#FEF3C7', text: '#D97706', label: 'Meetup' },
  Workshop: { bg: '#DBEAFE', text: '#1D4ED8', label: 'Workshop' },
  Webinar: { bg: '#F3E8FF', text: '#7C3AED', label: 'Webinar' },
  Meetup: { bg: '#FEF3C7', text: '#D97706', label: 'Meetup' },
};

function formatDate(d) {
  if (!d) return '-';
  try { return new Date(d).toLocaleDateString('it-CH', { day: 'numeric', month: 'long', year: 'numeric' }); } catch { return d; }
}

function daysUntil(d) {
  if (!d) return null;
  try { const diff = Math.ceil((new Date(d) - Date.now()) / 86400000); return diff; } catch { return null; }
}

function fillRateColor(rate) {
  if (rate >= 70) return '#059669';
  if (rate >= 40) return '#D97706';
  return '#DC2626';
}

export default function EventsPage() {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [listTab, setListTab] = useState('upcoming');
  const [detailTab, setDetailTab] = useState('dashboard');
  const [registrations, setRegistrations] = useState([]);
  const [showRegForm, setShowRegForm] = useState(false);
  const [regName, setRegName] = useState('');
  const [regEmail, setRegEmail] = useState('');
  const [showMenu, setShowMenu] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editFields, setEditFields] = useState({});
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [createForm, setCreateForm] = useState({ title: '', event_type: 'E1', event_date: '', location: '', max_participants: 50, description: '' });

  // Progettazione state
  const [eventTypes, setEventTypes] = useState([]);
  const [configObjectives, setConfigObjectives] = useState([]);
  const [configTargets, setConfigTargets] = useState([]);
  const [progForm, setProgForm] = useState({});
  const [agendaSlots, setAgendaSlots] = useState([]);
  const [materials, setMaterials] = useState({ presentation: false, handout: false, recording: false, assessment: false });
  const [eventMode, setEventMode] = useState('in_presenza');
  const [earlyBirdEnabled, setEarlyBirdEnabled] = useState(false);
  const [savingProg, setSavingProg] = useState(false);
  const [speakerSearch, setSpeakerSearch] = useState({});
  const [speakerResults, setSpeakerResults] = useState({});

  // Promo plan state
  const [promoPlan, setPromoPlan] = useState(null);
  const [promoLoading, setPromoLoading] = useState(false);

  // Cascade dialog state
  const [showCascadeDialog, setShowCascadeDialog] = useState(false);
  const [cascadeData, setCascadeData] = useState(null);
  const [configDialogType, setConfigDialogType] = useState(null);

  const loadEvents = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/events?sort=-event_date');
      const items = (res.data || []).map(e => {
        const fillRes = e.fill_rate || (e.registrations_count != null && e.max_participants
          ? Math.round((e.registrations_count / e.max_participants) * 100)
          : 0);
        return { ...e, fillRate: fillRes, registered: e.registrations_count || 0, capacity: e.max_participants || 50 };
      });
      setEvents(items);
      if (items.length > 0 && !selectedId) setSelectedId(items[0].id);
    } catch { toast.error('Errore caricamento eventi'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadEvents(); }, [loadEvents]);

  // Load config for Progettazione tab
  useEffect(() => {
    const loadConfig = async () => {
      try {
        const [etRes, objRes, tgtRes] = await Promise.all([
          api.get('/content-config/event-types').catch(() => ({ data: [] })),
          api.get('/content-config/objectives').catch(() => ({ data: [] })),
          api.get('/content-config/targets').catch(() => ({ data: [] })),
        ]);
        setEventTypes(etRes.data || []);
        setConfigObjectives(objRes.data || []);
        setConfigTargets(tgtRes.data || []);
      } catch {}
    };
    loadConfig();
  }, []);

  const selectedEvent = events.find(e => e.id === selectedId);

  const loadRegistrations = useCallback(async () => {
    if (!selectedId) return;
    try {
      const res = await api.get(`/events/${selectedId}/registrations`);
      setRegistrations(res.data || []);
    } catch { setRegistrations([]); }
  }, [selectedId]);

  useEffect(() => {
    if (detailTab === 'attendees') loadRegistrations();
  }, [detailTab, selectedId, loadRegistrations]);

  // Load promo plan when dashboard tab active
  useEffect(() => {
    if (detailTab === 'dashboard' && selectedId) {
      api.get(`/events/${selectedId}/promo-plan`).then(res => {
        // Handle both { actions: [...] } and direct array responses
        const actions = res.data?.actions || res.data?.items || (Array.isArray(res.data) ? res.data : null);
        setPromoPlan(actions && actions.length > 0 ? actions : null);
      }).catch(() => setPromoPlan(null));
    }
  }, [detailTab, selectedId]);

  // Init Progettazione form when tab or event changes
  useEffect(() => {
    if (detailTab === 'design' && selectedEvent) {
      setProgForm({
        event_type: selectedEvent.event_type || '',
        name: selectedEvent.name || selectedEvent.title || '',
        tagline: selectedEvent.tagline || '',
        description: selectedEvent.description || '',
        objective_id: selectedEvent.objective_id || '',
        target_ids: selectedEvent.target_ids || [],
        language: selectedEvent.language || 'it',
        edition: selectedEvent.edition || 1,
        event_date: selectedEvent.event_date?.split('T')[0] || '',
        start_time: selectedEvent.start_time || '',
        end_time: selectedEvent.end_time || '',
        location: selectedEvent.location || '',
        meeting_link: selectedEvent.meeting_link || '',
        capacity: selectedEvent.max_participants || selectedEvent.capacity || 50,
        price: selectedEvent.price || '',
        currency: selectedEvent.currency || 'CHF',
        early_bird_price: selectedEvent.early_bird_price || '',
        early_bird_deadline: selectedEvent.early_bird_deadline || '',
      });
      setEventMode(selectedEvent.mode || selectedEvent.event_mode || 'in_presenza');
      setEarlyBirdEnabled(!!selectedEvent.early_bird_price);
      setAgendaSlots(selectedEvent.agenda || []);
      setMaterials(selectedEvent.materials || { presentation: false, handout: false, recording: false, assessment: false });
    }
  }, [detailTab, selectedEvent]);

  const now = new Date();
  const upcoming = events.filter(e => !e.event_date || new Date(e.event_date) >= now);
  const past = events.filter(e => e.event_date && new Date(e.event_date) < now);
  const drafts = events.filter(e => e.status === 'draft');
  const visibleEvents = listTab === 'upcoming' ? upcoming : listTab === 'past' ? past : drafts;

  const handleRegister = async () => {
    if (!regName.trim() || !regEmail.trim() || !selectedId) return;
    try {
      await api.post(`/events/${selectedId}/register`, { contact_name: regName, contact_email: regEmail });
      toast.success('Partecipante registrato');
      setRegName(''); setRegEmail(''); setShowRegForm(false);
      loadRegistrations();
      loadEvents();
    } catch { toast.error('Errore registrazione'); }
  };

  const handleCheckIn = async (regId, checked) => {
    try {
      await api.patch(`/events/${selectedId}/registrations/${regId}`, { checked_in: checked });
      setRegistrations(prev => prev.map(r => r.id === regId ? { ...r, checked_in: checked } : r));
      toast.success(checked ? 'Check-in effettuato' : 'Check-in rimosso');
    } catch { toast.error('Errore check-in'); }
  };

  const handleDuplicate = async () => {
    if (!selectedId || !selectedEvent) return;
    try {
      const res = await api.post(`/events/${selectedId}/duplicate`);
      toast.success(`Evento duplicato: ${res.data?.name || 'copia creata'}`);
      loadEvents();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore duplicazione evento'); }
    setShowMenu(false);
  };

  const handleSaveLogistics = async () => {
    if (!selectedId) return;
    try {
      await api.patch(`/events/${selectedId}`, editFields);
      toast.success('Salvato');
      setEditMode(false);
      loadEvents();
    } catch { toast.error('Errore salvataggio'); }
  };

  const handleCreateEvent = async () => {
    if (!createForm.title.trim()) return;
    try {
      await api.post('/events', createForm);
      toast.success('Evento creato');
      setShowCreateDialog(false);
      setCreateForm({ title: '', event_type: 'E1', event_date: '', location: '', max_participants: 50, description: '' });
      loadEvents();
    } catch { toast.error('Errore creazione evento'); }
  };

  const handleExportCSV = async () => {
    if (!selectedId) return;
    try {
      const res = await api.get(`/events/${selectedId}/registrations`);
      const regs = res.data || [];
      if (regs.length === 0) { toast.info('Nessun iscritto da esportare'); return; }
      const headers = ['Nome', 'Email', 'Check-in', 'Data registrazione'];
      const rows = regs.map(r => [r.contact_name || '', r.contact_email || '', r.checked_in ? 'Si' : 'No', r.registered_at || '']);
      const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `iscritti_${selectedEvent?.title || 'evento'}.csv`; a.click();
      URL.revokeObjectURL(url);
      toast.success(`CSV esportato: ${regs.length} iscritti`);
    } catch { toast.error('Errore esportazione'); }
  };

  const handlePromote = () => {
    navigate(`/content-studio?event=${selectedId}&topic=${encodeURIComponent((selectedEvent?.name || selectedEvent?.title || '') + ' — promozione')}`);
  };

  // Save Progettazione
  const handleSaveProgettazione = async () => {
    if (!selectedId) return;
    setSavingProg(true);
    try {
      const payload = {
        ...progForm,
        mode: eventMode,
        materials,
        agenda: agendaSlots,
        early_bird_price: earlyBirdEnabled ? progForm.early_bird_price : null,
        early_bird_deadline: earlyBirdEnabled ? progForm.early_bird_deadline : null,
      };
      const res = await api.patch(`/events/${selectedId}`, payload);
      if (res.data?.requires_confirmation) {
        setCascadeData(res.data);
        setShowCascadeDialog(true);
      } else {
        toast.success('Evento salvato');
        loadEvents();
      }
    } catch { toast.error('Errore salvataggio'); }
    setSavingProg(false);
  };

  // Cascade handler
  const handleCascade = async (action) => {
    try {
      await api.post(`/events/${selectedId}/cascade-action`, {
        action,
        event_updates: { ...progForm, mode: eventMode, materials, agenda: agendaSlots },
        new_event_date: progForm.event_date,
      });
      toast.success('Evento aggiornato');
      setShowCascadeDialog(false);
      setCascadeData(null);
      loadEvents();
    } catch { toast.error('Errore cascata'); }
  };

  // Generate promo plan
  const handleGenPromoPlan = async () => {
    if (!selectedId) return;
    setPromoLoading(true);
    try {
      const res = await api.post(`/events/${selectedId}/promo-plan`);
      setPromoPlan(res.data?.actions || res.data || []);
      toast.success('Piano promozione generato');
    } catch { toast.error('Errore generazione piano'); }
    setPromoLoading(false);
  };

  // Agenda slot management
  const addAgendaSlot = () => {
    setAgendaSlots(prev => [...prev, { start_time: '', end_time: '', title: '', description: '', speaker: '' }]);
  };
  const updateSlot = (idx, field, value) => {
    setAgendaSlots(prev => prev.map((s, i) => i === idx ? { ...s, [field]: value } : s));
  };
  const removeSlot = (idx) => { setAgendaSlots(prev => prev.filter((_, i) => i !== idx)); };

  // Speaker autocomplete
  const searchSpeaker = async (idx, query) => {
    setSpeakerSearch(p => ({ ...p, [idx]: query }));
    if (!query.trim()) { setSpeakerResults(p => ({ ...p, [idx]: [] })); return; }
    try {
      const res = await api.get(`/crm/contacts?search=${encodeURIComponent(query)}&limit=5`);
      setSpeakerResults(p => ({ ...p, [idx]: res.data?.contacts || res.data || [] }));
    } catch { setSpeakerResults(p => ({ ...p, [idx]: [] })); }
  };

  if (loading && events.length === 0) {
    return (
      <div className="flex items-center justify-center h-full" style={{ backgroundColor: '#FAFBFC' }}
        data-testid="events-loading">
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#2563EB' }} />
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100vh-64px)]" data-testid="events-page" style={{ backgroundColor: '#FAFBFC' }}>
      {/* ═══ Left Panel - Event List (280px) ═══ */}
      <div className="w-[280px] flex-shrink-0 flex flex-col" style={{ borderRight: '1px solid #E5E7EB', backgroundColor: '#FFFFFF' }}>
        <div className="p-4 flex items-center justify-between flex-shrink-0" style={{ borderBottom: '1px solid #E5E7EB' }}>
          <h1 className="text-[16px] font-bold" style={{ color: '#1B2B4B' }}>Eventi</h1>
          <button className="p-2 rounded-md hover:bg-blue-50 transition-colors" style={{ color: '#2563EB' }}
            onClick={() => setShowCreateDialog(true)} data-testid="create-event-btn">
            <Plus size={18} />
          </button>
        </div>

        {/* List Tabs */}
        <div className="flex flex-shrink-0" style={{ borderBottom: '1px solid #E5E7EB' }}>
          {[
            { key: 'upcoming', label: `Prossimi (${upcoming.length})` },
            { key: 'past', label: `Passati (${past.length})` },
            { key: 'drafts', label: `Bozze (${drafts.length})` },
          ].map(t => (
            <button key={t.key} onClick={() => setListTab(t.key)}
              className="flex-1 py-2.5 text-[12px] font-medium transition-colors"
              style={{
                color: listTab === t.key ? '#2563EB' : '#7C8CA2',
                borderBottom: listTab === t.key ? '2px solid #2563EB' : '2px solid transparent',
              }}
              data-testid={`list-tab-${t.key}`}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Event Cards */}
        <div className="flex-1 overflow-y-auto">
          {visibleEvents.length === 0 ? (
            <EmptyState type="events" onAction={() => setShowCreate(true)} />
          ) : visibleEvents.map(ev => {
            const tc = TYPE_COLORS[ev.event_type] || TYPE_COLORS.E1;
            const countdown = daysUntil(ev.event_date);
            const isLow = ev.fillRate < 40 && countdown !== null && countdown > 0 && countdown < 20;
            return (
              <button key={ev.id} onClick={() => { setSelectedId(ev.id); setDetailTab('dashboard'); }}
                className="w-full text-left p-3 transition-colors"
                style={{
                  borderBottom: '1px solid #E5E7EB',
                  backgroundColor: selectedId === ev.id ? '#EFF6FF' : 'transparent',
                  borderLeft: isLow ? '3px solid #DC2626' : selectedId === ev.id ? '3px solid #2563EB' : '3px solid transparent',
                }}
                data-testid={`event-card-${ev.id}`}>
                <div className="flex items-center gap-2 mb-1">
                  <span className="px-2 py-0.5 rounded text-[10px] font-medium" style={{ backgroundColor: tc.bg, color: tc.text }}>
                    {tc.label}
                  </span>
                  {countdown !== null && countdown > 0 && (
                    <span className="text-[10px] font-bold" style={{ color: countdown < 7 ? '#DC2626' : '#7C8CA2' }}>
                      D-{countdown}
                    </span>
                  )}
                </div>
                <p className="text-[13px] font-semibold truncate" style={{ color: '#1B2B4B' }}>{ev.name}</p>
                <p className="text-[11px] mt-0.5" style={{ color: '#7C8CA2' }}>{formatDate(ev.event_date)}</p>
                <div className="mt-2 flex items-center gap-2">
                  <div className="flex-1 h-1 rounded-full" style={{ backgroundColor: '#E5E7EB' }}>
                    <div className="h-1 rounded-full transition-all" style={{ width: `${Math.min(ev.fillRate, 100)}%`, backgroundColor: fillRateColor(ev.fillRate) }} />
                  </div>
                  <span className="text-[10px] font-medium" style={{ color: fillRateColor(ev.fillRate) }}>
                    {ev.fillRate}%
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* ═══ Right Panel - Event Detail ═══ */}
      <div className="flex-1 flex flex-col" style={{ backgroundColor: '#FFFFFF' }}>
        {selectedEvent ? (
          <>
            {/* Event Header */}
            <div className="p-5 flex-shrink-0" style={{ borderBottom: '1px solid #E5E7EB' }}>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h2 className="text-[20px] font-bold" style={{ color: '#1B2B4B' }} data-testid="event-title">
                    {selectedEvent.name}
                  </h2>
                  <div className="flex items-center gap-3 mt-1">
                    <span className="text-[13px]" style={{ color: '#7C8CA2' }}>
                      {formatDate(selectedEvent.event_date)}
                    </span>
                    {(() => { const tc = TYPE_COLORS[selectedEvent.event_type] || TYPE_COLORS.E1; return (
                      <span className="px-2 py-0.5 rounded text-[11px] font-medium" style={{ backgroundColor: tc.bg, color: tc.text }}>
                        {tc.label}
                      </span>
                    ); })()}
                    {selectedEvent.location && (
                      <span className="flex items-center gap-1 text-[12px]" style={{ color: '#7C8CA2' }}>
                        <MapPin size={12} />{selectedEvent.location}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <div className="flex items-baseline justify-end gap-2">
                      <span className="text-[48px] font-bold leading-none" style={{ color: fillRateColor(selectedEvent.fillRate) }}
                        data-testid="fill-rate-big">
                        {selectedEvent.fillRate}%
                      </span>
                      {(() => {
                        const d = daysUntil(selectedEvent.event_date || selectedEvent.date);
                        return d !== null && d > 0 ? (
                          <span className="rounded-lg px-2.5 py-1 text-[14px] font-bold" 
                            style={{ backgroundColor: d < 7 ? '#FEE2E2' : d < 14 ? '#FEF3C7' : '#F3F4F6', 
                                     color: d < 7 ? '#DC2626' : d < 14 ? '#D97706' : '#6B7280' }}
                            data-testid="countdown-badge">
                            D-{d}
                          </span>
                        ) : d !== null && d <= 0 ? (
                          <span className="rounded-lg px-2.5 py-1 text-[14px] font-bold"
                            style={{ backgroundColor: '#D1FAE5', color: '#059669' }}>
                            {d === 0 ? 'OGGI' : 'Concluso'}
                          </span>
                        ) : null;
                      })()}
                    </div>
                    <p className="text-[12px] mt-1" style={{ color: '#7C8CA2' }}>
                      {selectedEvent.registered}/{selectedEvent.capacity} partecipanti
                    </p>
                    <div className="mt-1 h-2 w-36 rounded-full" style={{ backgroundColor: '#E5E7EB' }}>
                      <div className="h-2 rounded-full transition-all" style={{
                        width: `${Math.min(selectedEvent.fillRate, 100)}%`,
                        backgroundColor: fillRateColor(selectedEvent.fillRate),
                      }} />
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 mt-3">
                <button className="px-3 py-2 rounded-lg text-[12px] font-medium text-white hover:opacity-90"
                  style={{ backgroundColor: '#2563EB' }}
                  onClick={handlePromote}
                  data-testid="promote-event-btn">
                  Promuovi evento
                </button>
                <button onClick={() => { setDetailTab('attendees'); setShowRegForm(true); }}
                  className="px-3 py-2 rounded-lg text-[12px] font-medium hover:bg-gray-50"
                  style={{ border: '1px solid #E5E7EB', color: '#1B2B4B' }}
                  data-testid="register-btn">
                  Registra partecipante
                </button>
                <button className="px-3 py-2 rounded-lg text-[12px] font-medium hover:bg-gray-50"
                  style={{ border: '1px solid #E5E7EB', color: '#1B2B4B' }}
                  onClick={() => setEditMode(true)}>
                  <Edit2 size={14} className="inline mr-1" />Modifica
                </button>
                <div className="relative ml-auto">
                  <button onClick={() => setShowMenu(!showMenu)}
                    className="p-2 rounded-md hover:bg-gray-100 transition-colors">
                    <MoreHorizontal size={18} style={{ color: '#7C8CA2' }} />
                  </button>
                  {showMenu && (
                    <div className="absolute right-0 top-full mt-1 w-48 rounded-lg bg-white shadow-lg z-10"
                      style={{ border: '1px solid #E5E7EB' }}>
                      <button onClick={handleDuplicate}
                        className="w-full flex items-center gap-2 px-4 py-2.5 text-[13px] text-left hover:bg-gray-50" style={{ color: '#1B2B4B' }}>
                        <Copy size={14} />Duplica evento
                      </button>
                      <button onClick={async () => { setShowMenu(false); try { await api.patch(`/events/${selectedId}`, { status: 'archived' }); toast.success('Evento archiviato'); loadEvents(); } catch (e) { toast.error(e.response?.data?.detail || 'Errore archiviazione'); } }}
                        className="w-full flex items-center gap-2 px-4 py-2.5 text-[13px] text-left hover:bg-gray-50" style={{ color: '#1B2B4B' }}>
                        <Archive size={14} />Archivia evento
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* ProcessBar */}
            <div className="px-5 pt-3">
              <ProcessBar steps={['Progetta', 'Promuovi', 'Esegui', 'Follow-up']}
                step={detailTab === 'design' ? 1 : detailTab === 'dashboard' ? 2 : detailTab === 'attendees' ? 2 : 3} />
            </div>

            {/* 4 Detail Tabs (V8.1: reduced from 6) */}
            <Tabs value={detailTab} onValueChange={setDetailTab} className="flex-1 flex flex-col overflow-hidden">
              <TabsList className="px-5 pt-1 justify-start bg-transparent border-b rounded-none gap-0" style={{ borderColor: '#E5E7EB' }}>
                {[
                  { v: 'dashboard', l: 'Dashboard' }, { v: 'attendees', l: 'Iscritti' },
                  { v: 'design', l: 'Progettazione' },
                  ...(selectedEvent && new Date(selectedEvent.event_date) < new Date() ? [{ v: 'postevent', l: 'Post-evento' }] : []),
                ].map(t => (
                  <TabsTrigger key={t.v} value={t.v}
                    className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#2563EB] data-[state=active]:text-[#2563EB] text-[13px] px-4 py-2.5"
                    data-testid={`detail-tab-${t.v}`}>
                    {t.l}
                  </TabsTrigger>
                ))}
              </TabsList>

              <div className="flex-1 overflow-y-auto p-5" style={{ backgroundColor: '#FAFBFC' }}>
                {/* Dashboard */}
                <TabsContent value="dashboard" className="mt-0">
                  <div className="grid grid-cols-3 gap-4">
                    {['LinkedIn', 'Email', 'Diretto'].map((src, i) => {
                      const srcCount = registrations.filter(r => (r.source || '').toLowerCase().includes(src.toLowerCase())).length;
                      return (
                        <div key={src} className="rounded-lg p-4" style={{ backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB' }}>
                          <p className="text-[12px] font-medium" style={{ color: '#7C8CA2' }}>{src}</p>
                          <p className="text-[24px] font-bold mt-1" style={{ color: '#1B2B4B' }}>{srcCount}</p>
                          <p className="text-[11px]" style={{ color: '#7C8CA2' }}>registrazioni</p>
                        </div>
                      );
                    })}
                  </div>
                  <div className="mt-4 rounded-lg p-4" style={{ backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB' }}>
                    <p className="text-[14px] font-semibold" style={{ color: '#1B2B4B' }}>Riepilogo</p>
                    <p className="text-[13px] mt-2" style={{ color: '#4B5563' }}>
                      Fill rate: <strong>{selectedEvent.fillRate}%</strong> — {selectedEvent.registered} registrati su {selectedEvent.capacity} posti.
                    </p>
                  </div>

                  {/* Piano promozione */}
                  <div className="mt-4 rounded-lg p-4" style={{ backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB' }} data-testid="promo-plan-section">
                    <p className="text-[14px] font-semibold mb-3" style={{ color: '#1B2B4B' }}>Piano promozione</p>
                    {!promoPlan || !Array.isArray(promoPlan) || promoPlan.length === 0 ? (
                      <Button onClick={handleGenPromoPlan} disabled={promoLoading}
                        className="text-white" style={{ backgroundColor: '#7C3AED' }} data-testid="gen-promo-plan">
                        {promoLoading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1.5" />}
                        Genera piano promozione
                      </Button>
                    ) : (
                      <div className="space-y-2">
                        {(Array.isArray(promoPlan) ? promoPlan : []).map((action, i) => (
                          <div key={i} className="flex items-center gap-3 py-2 border-b last:border-0" style={{ borderColor: '#F3F4F6' }}
                            data-testid={`promo-action-${i}`}>
                            <span className="text-sm">{action.status === 'generated' || action.status === 'done' ? '\u2705' : '\u23F3'}</span>
                            <div className="flex-1 min-w-0">
                              <span className="text-[13px] font-medium" style={{ color: '#1B2B4B' }}>{action.type || action.title || action.action_type}</span>
                              <span className="text-[11px] ml-2" style={{ color: '#7C8CA2' }}>
                                {action.status === 'generated' || action.status === 'done' ? 'Generato' : 'Da generare'}
                                {action.scheduled_date ? ` ${new Date(action.scheduled_date).toLocaleDateString('it-CH', { day: 'numeric', month: 'numeric' })}` : ''}
                              </span>
                            </div>
                            {action.status !== 'generated' && action.status !== 'done' && (
                              <button onClick={() => navigate(`/content-studio?event=${selectedId}&objective=event_promo`)}
                                className="text-[12px] font-medium hover:underline" style={{ color: '#2563EB' }}>
                                Genera nel Content Studio &rarr;
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </TabsContent>

                {/* Iscritti */}
                <TabsContent value="attendees" className="mt-0">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-[13px] font-medium" style={{ color: '#1B2B4B' }}>
                      {registrations.length} iscritti
                    </span>
                    <div className="flex gap-2">
                      <button onClick={() => setShowRegForm(true)}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] font-medium text-white hover:opacity-90"
                        style={{ backgroundColor: '#2563EB' }} data-testid="quick-register-btn">
                        <Plus size={14} />Registra
                      </button>
                      <button className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] font-medium hover:bg-gray-50"
                        style={{ border: '1px solid #E5E7EB', color: '#7C8CA2' }}
                        onClick={handleExportCSV}
                        data-testid="export-csv-btn">
                        <Download size={14} />Esporta
                      </button>
                    </div>
                  </div>
                  {registrations.length === 0 ? (
                    <div className="text-center py-8">
                      <Users size={32} className="mx-auto mb-2" style={{ color: '#D1D5DB' }} />
                      <p className="text-[13px]" style={{ color: '#7C8CA2' }}>Nessun iscritto</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {registrations.map(r => (
                        <div key={r.id || r.contact_email}
                          className="flex items-center gap-3 rounded-lg p-3"
                          style={{ backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB' }}
                          data-testid={`reg-${r.id || r.contact_email}`}>
                          <div className="flex h-9 w-9 items-center justify-center rounded-full text-[11px] font-bold text-white flex-shrink-0"
                            style={{ backgroundColor: '#2563EB' }}>
                            {(r.contact_name || r.full_name || '??').split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                          </div>
                          <div className="flex-1 min-w-0">
                            <button onClick={() => r.contact_id && navigate(`/contacts`)}
                              className="text-[13px] font-semibold hover:text-[#2563EB] transition-colors"
                              style={{ color: '#1B2B4B' }}>
                              {r.contact_name || r.full_name || r.contact_email}
                            </button>
                            <p className="text-[11px]" style={{ color: '#7C8CA2' }}>
                              {r.company || '-'} · {r.source || '-'}
                            </p>
                          </div>
                          <span className="px-2 py-0.5 rounded text-[10px] font-medium"
                            style={{
                              backgroundColor: r.status === 'confirmed' ? '#D1FAE5' : '#FEF3C7',
                              color: r.status === 'confirmed' ? '#059669' : '#D97706',
                            }}>
                            {r.status === 'confirmed' ? 'Confermato' : 'In attesa'}
                          </span>
                          <div className="flex items-center gap-2">
                            <Checkbox
                              checked={r.checked_in === true}
                              onCheckedChange={(val) => handleCheckIn(r.id, !!val)}
                              data-testid={`checkin-${r.id || r.contact_email}`}
                            />
                            <span className="text-[11px]" style={{ color: r.checked_in ? '#059669' : '#7C8CA2' }}>
                              {r.checked_in ? 'Presente' : 'Check-in'}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </TabsContent>

                {/* Post-evento (visible only for past events) */}
                <TabsContent value="postevent" className="mt-0">
                  <div className="rounded-lg p-4" style={{ backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB' }}>
                    <p className="text-[14px] font-semibold" style={{ color: '#1B2B4B' }}>Contenuti post-evento</p>
                    <p className="text-[13px] mt-2" style={{ color: '#7C8CA2' }}>
                      Dopo l'evento potrai generare: email di recap ai partecipanti, post con i takeaway principali, e sequenza follow-up per i no-show.
                    </p>
                    <p className="text-[12px] mt-1" style={{ color: '#9CA3AF' }}>
                      Questa funzionalità sarà attiva dopo il primo evento completato.
                    </p>
                  </div>
                  {selectedEvent && new Date(selectedEvent.event_date) < new Date() && (
                    <button className="mt-3 px-4 py-2 rounded-lg text-[13px] font-medium hover:opacity-90"
                      style={{ color: '#2563EB' }}
                      onClick={() => navigate(`/content-studio?event=${selectedId}&topic=${encodeURIComponent('recap evento ' + (selectedEvent?.name || ''))}`)}>
                      Crea post recap nel Content Studio &rarr;
                    </button>
                  )}
                  <div className="mt-4 rounded-lg p-4" style={{ backgroundColor: '#F9FAFB', border: '1px solid #E5E7EB' }}>
                    <p className="text-[13px] font-medium" style={{ color: '#1B2B4B' }}>Trascrizioni Fireflies</p>
                    <p className="text-[12px] mt-1" style={{ color: '#9CA3AF' }}>
                      Le trascrizioni delle call verranno mostrate qui quando collegate.
                    </p>
                  </div>
                </TabsContent>

                {/* Logistica */}
                {/* Progettazione */}
                <TabsContent value="design" className="mt-0">
                  <div className="space-y-4" data-testid="progettazione-tab">
                    {/* IDENTITA */}
                    <Collapsible defaultOpen={true}>
                      <CollapsibleTrigger asChild>
                        <button className="w-full flex items-center justify-between py-2.5 px-4 text-[12px] font-semibold uppercase tracking-wider hover:bg-gray-50 rounded-t-lg"
                          style={{ color: 'var(--color-primary)' }} data-testid="section-identita">
                          <span>Identita</span><ChevronDown className="w-4 h-4" />
                        </button>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <div className="px-4 pb-4 space-y-3 rounded-lg bg-white border" style={{ borderColor: '#E5E7EB' }}>
                          <div className="grid grid-cols-2 gap-3 pt-3">
                            <div>
                              <div className="flex items-center justify-between mb-1">
                                <label className="text-[11px] font-medium" style={{ color: '#7C8CA2' }}>Tipo evento</label>
                                <button onClick={() => setConfigDialogType('event-types')}
                                  className="text-[10px]" style={{ color: '#7C8CA2' }}
                                  onMouseEnter={e => e.currentTarget.style.color = '#2563EB'}
                                  onMouseLeave={e => e.currentTarget.style.color = '#7C8CA2'}
                                  data-testid="gear-event-types">
                                  &#9881; modifica
                                </button>
                              </div>
                              {eventTypes.length > 0 ? (
                                <Select value={progForm.event_type || ''} onValueChange={v => setProgForm(p => ({ ...p, event_type: v }))}>
                                  <SelectTrigger className="text-xs h-9" data-testid="prog-event-type"><SelectValue placeholder="Tipo..." /></SelectTrigger>
                                  <SelectContent>{eventTypes.map(t => <SelectItem key={t.id} value={t.id}>{t.label || t.name}</SelectItem>)}</SelectContent>
                                </Select>
                              ) : (
                                <select value={progForm.event_type || ''} onChange={e => setProgForm(p => ({ ...p, event_type: e.target.value }))}
                                  className="w-full rounded-md border text-xs h-9 px-3" style={{ borderColor: '#E5E7EB' }}>
                                  <option value="E1">Workshop</option><option value="E2">Webinar</option>
                                  <option value="E3">Meetup</option><option value="E4">Conferenza</option>
                                </select>
                              )}
                            </div>
                            <div>
                              <label className="text-[11px] font-medium block mb-1" style={{ color: '#7C8CA2' }}>Nome</label>
                              <Input value={progForm.name || ''} onChange={e => setProgForm(p => ({ ...p, name: e.target.value }))}
                                className="text-xs h-9" data-testid="prog-name" />
                            </div>
                          </div>
                          <div>
                            <label className="text-[11px] font-medium block mb-1" style={{ color: '#7C8CA2' }}>Tagline</label>
                            <Input value={progForm.tagline || ''} onChange={e => setProgForm(p => ({ ...p, tagline: e.target.value }))}
                              placeholder="Una frase che cattura l'essenza" className="text-xs h-9" data-testid="prog-tagline" />
                          </div>
                          <div>
                            <label className="text-[11px] font-medium block mb-1" style={{ color: '#7C8CA2' }}>Descrizione</label>
                            <Textarea value={progForm.description || ''} onChange={e => setProgForm(p => ({ ...p, description: e.target.value }))}
                              rows={4} className="text-xs" data-testid="prog-description" />
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <div className="flex items-center justify-between mb-1">
                                <label className="text-[11px] font-medium" style={{ color: '#7C8CA2' }}>Obiettivo</label>
                                <button onClick={() => setConfigDialogType('objectives')}
                                  className="text-[10px]" style={{ color: '#7C8CA2' }}
                                  onMouseEnter={e => e.currentTarget.style.color = '#2563EB'}
                                  onMouseLeave={e => e.currentTarget.style.color = '#7C8CA2'}
                                  data-testid="gear-objectives-events">
                                  &#9881; modifica
                                </button>
                              </div>
                              <Select value={progForm.objective_id || ''} onValueChange={v => setProgForm(p => ({ ...p, objective_id: v }))}>
                                <SelectTrigger className="text-xs h-9" data-testid="prog-objective"><SelectValue placeholder="Obiettivo..." /></SelectTrigger>
                                <SelectContent>{configObjectives.map(o => <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>)}</SelectContent>
                              </Select>
                            </div>
                            <div>
                              <label className="text-[11px] font-medium block mb-1" style={{ color: '#7C8CA2' }}>Lingua</label>
                              <Select value={progForm.language || 'it'} onValueChange={v => setProgForm(p => ({ ...p, language: v }))}>
                                <SelectTrigger className="text-xs h-9" data-testid="prog-language"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="it">Italiano</SelectItem>
                                  <SelectItem value="en">English</SelectItem>
                                  <SelectItem value="de">Deutsch</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                          <div>
                            <div className="flex items-center justify-between mb-1.5">
                              <label className="text-[11px] font-medium" style={{ color: '#7C8CA2' }}>Target</label>
                              <button onClick={() => setConfigDialogType('targets')}
                                className="text-[10px]" style={{ color: '#7C8CA2' }}
                                onMouseEnter={e => e.currentTarget.style.color = '#2563EB'}
                                onMouseLeave={e => e.currentTarget.style.color = '#7C8CA2'}
                                data-testid="gear-targets-events">
                                &#9881; modifica
                              </button>
                            </div>
                            <div className="flex flex-wrap gap-1.5">
                              {configTargets.map(t => (
                                <Badge key={t.id} variant={(progForm.target_ids || []).includes(t.id) ? 'default' : 'outline'}
                                  className="cursor-pointer text-[11px] px-2 py-0.5"
                                  onClick={() => setProgForm(p => ({
                                    ...p, target_ids: (p.target_ids || []).includes(t.id)
                                      ? (p.target_ids || []).filter(x => x !== t.id) : [...(p.target_ids || []), t.id]
                                  }))}>{t.label}</Badge>
                              ))}
                            </div>
                          </div>
                          <div>
                            <label className="text-[11px] font-medium block mb-1" style={{ color: '#7C8CA2' }}>Edizione</label>
                            <Input type="number" value={progForm.edition || ''} onChange={e => setProgForm(p => ({ ...p, edition: parseInt(e.target.value) || 1 }))}
                              placeholder="Se evento ricorrente: #4" className="text-xs h-9 w-24" data-testid="prog-edition" />
                          </div>
                        </div>
                      </CollapsibleContent>
                    </Collapsible>

                    {/* LOGISTICA */}
                    <Collapsible defaultOpen={false}>
                      <CollapsibleTrigger asChild>
                        <button className="w-full flex items-center justify-between py-2.5 px-4 text-[12px] font-semibold uppercase tracking-wider hover:bg-gray-50 rounded-t-lg"
                          style={{ color: 'var(--color-primary)' }} data-testid="section-logistica">
                          <span>Logistica</span><ChevronDown className="w-4 h-4" />
                        </button>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <div className="px-4 pb-4 space-y-3 rounded-lg bg-white border" style={{ borderColor: '#E5E7EB' }}>
                          <div className="grid grid-cols-3 gap-3 pt-3">
                            <div>
                              <label className="text-[11px] font-medium block mb-1" style={{ color: '#7C8CA2' }}>Data</label>
                              <Input type="date" value={progForm.event_date || ''} onChange={e => setProgForm(p => ({ ...p, event_date: e.target.value }))}
                                className="text-xs h-9" data-testid="prog-date" />
                            </div>
                            <div>
                              <label className="text-[11px] font-medium block mb-1" style={{ color: '#7C8CA2' }}>Ora inizio</label>
                              <Input type="time" value={progForm.start_time || ''} onChange={e => setProgForm(p => ({ ...p, start_time: e.target.value }))}
                                className="text-xs h-9" data-testid="prog-start-time" />
                            </div>
                            <div>
                              <label className="text-[11px] font-medium block mb-1" style={{ color: '#7C8CA2' }}>Ora fine</label>
                              <Input type="time" value={progForm.end_time || ''} onChange={e => setProgForm(p => ({ ...p, end_time: e.target.value }))}
                                className="text-xs h-9" data-testid="prog-end-time" />
                            </div>
                          </div>
                          <div>
                            <label className="text-[11px] font-medium block mb-1.5" style={{ color: '#7C8CA2' }}>Modalita</label>
                            <div className="grid grid-cols-3 gap-1.5">
                              {[{ v: 'in_presenza', l: 'In presenza' }, { v: 'online', l: 'Online' }, { v: 'ibrido', l: 'Ibrido' }].map(m => (
                                <button key={m.v} onClick={() => setEventMode(m.v)}
                                  className={`py-2 rounded-md text-xs font-medium border transition-colors ${eventMode === m.v ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 hover:border-gray-300'}`}
                                  data-testid={`mode-${m.v}`}>{m.l}</button>
                              ))}
                            </div>
                          </div>
                          {(eventMode === 'in_presenza' || eventMode === 'ibrido') && (
                            <div>
                              <label className="text-[11px] font-medium block mb-1" style={{ color: '#7C8CA2' }}>Location</label>
                              <Input value={progForm.location || ''} onChange={e => setProgForm(p => ({ ...p, location: e.target.value }))}
                                placeholder="Es: Lugano, SUPSI" className="text-xs h-9" data-testid="prog-location" />
                            </div>
                          )}
                          {(eventMode === 'online' || eventMode === 'ibrido') && (
                            <div>
                              <label className="text-[11px] font-medium block mb-1" style={{ color: '#7C8CA2' }}>Link meeting</label>
                              <Input value={progForm.meeting_link || ''} onChange={e => setProgForm(p => ({ ...p, meeting_link: e.target.value }))}
                                placeholder="https://zoom.us/..." className="text-xs h-9" data-testid="prog-meeting-link" />
                            </div>
                          )}
                          <div className="grid grid-cols-3 gap-3">
                            <div>
                              <label className="text-[11px] font-medium block mb-1" style={{ color: '#7C8CA2' }}>Capienza</label>
                              <Input type="number" value={progForm.capacity || ''} onChange={e => setProgForm(p => ({ ...p, capacity: parseInt(e.target.value) || 0 }))}
                                className="text-xs h-9" data-testid="prog-capacity" />
                            </div>
                            <div>
                              <label className="text-[11px] font-medium block mb-1" style={{ color: '#7C8CA2' }}>Prezzo</label>
                              <Input value={progForm.price || ''} onChange={e => setProgForm(p => ({ ...p, price: e.target.value }))}
                                placeholder="0" className="text-xs h-9" data-testid="prog-price" />
                            </div>
                            <div>
                              <label className="text-[11px] font-medium block mb-1" style={{ color: '#7C8CA2' }}>Valuta</label>
                              <Select value={progForm.currency || 'CHF'} onValueChange={v => setProgForm(p => ({ ...p, currency: v }))}>
                                <SelectTrigger className="text-xs h-9"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="CHF">CHF</SelectItem>
                                  <SelectItem value="EUR">EUR</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                          <div>
                            <div className="flex items-center gap-2 mb-1.5">
                              <label className="text-[11px] font-medium" style={{ color: '#7C8CA2' }}>Early bird</label>
                              <button onClick={() => setEarlyBirdEnabled(!earlyBirdEnabled)}
                                className={`w-9 h-5 rounded-full transition-colors relative ${earlyBirdEnabled ? 'bg-blue-500' : 'bg-gray-300'}`}
                                data-testid="early-bird-toggle">
                                <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${earlyBirdEnabled ? 'translate-x-[18px]' : 'translate-x-0.5'}`} />
                              </button>
                            </div>
                            {earlyBirdEnabled && (
                              <div className="grid grid-cols-2 gap-3">
                                <div>
                                  <label className="text-[10px]" style={{ color: '#7C8CA2' }}>Prezzo early bird</label>
                                  <Input value={progForm.early_bird_price || ''} onChange={e => setProgForm(p => ({ ...p, early_bird_price: e.target.value }))}
                                    className="text-xs h-8" data-testid="prog-early-price" />
                                </div>
                                <div>
                                  <label className="text-[10px]" style={{ color: '#7C8CA2' }}>Scadenza</label>
                                  <Input type="date" value={progForm.early_bird_deadline || ''} onChange={e => setProgForm(p => ({ ...p, early_bird_deadline: e.target.value }))}
                                    className="text-xs h-8" data-testid="prog-early-deadline" />
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </CollapsibleContent>
                    </Collapsible>

                    {/* PROGRAMMA */}
                    <Collapsible defaultOpen={false}>
                      <CollapsibleTrigger asChild>
                        <button className="w-full flex items-center justify-between py-2.5 px-4 text-[12px] font-semibold uppercase tracking-wider hover:bg-gray-50 rounded-t-lg"
                          style={{ color: 'var(--color-primary)' }} data-testid="section-programma">
                          <span>Programma</span><ChevronDown className="w-4 h-4" />
                        </button>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <div className="px-4 pb-4 space-y-3 rounded-lg bg-white border" style={{ borderColor: '#E5E7EB' }}>
                          <div className="pt-3">
                            <label className="text-[11px] font-medium block mb-2" style={{ color: '#7C8CA2' }}>Agenda</label>
                            <div className="space-y-2">
                              {agendaSlots.map((slot, idx) => (
                                <div key={idx} className="rounded-lg border p-3 relative" style={{ borderColor: '#E5E7EB' }}
                                  data-testid={`agenda-slot-${idx}`}>
                                  <button onClick={() => removeSlot(idx)}
                                    className="absolute top-2 right-2 text-muted-foreground hover:text-red-500">
                                    <X className="w-3.5 h-3.5" />
                                  </button>
                                  <div className="grid grid-cols-4 gap-2 mb-2">
                                    <div>
                                      <label className="text-[10px]" style={{ color: '#7C8CA2' }}>Inizio</label>
                                      <Input type="time" value={slot.start_time || ''} onChange={e => updateSlot(idx, 'start_time', e.target.value)}
                                        className="text-xs h-7" />
                                    </div>
                                    <div>
                                      <label className="text-[10px]" style={{ color: '#7C8CA2' }}>Fine</label>
                                      <Input type="time" value={slot.end_time || ''} onChange={e => updateSlot(idx, 'end_time', e.target.value)}
                                        className="text-xs h-7" />
                                    </div>
                                    <div className="col-span-2">
                                      <label className="text-[10px]" style={{ color: '#7C8CA2' }}>Titolo</label>
                                      <Input value={slot.title || ''} onChange={e => updateSlot(idx, 'title', e.target.value)}
                                        className="text-xs h-7" placeholder="Titolo sessione" />
                                    </div>
                                  </div>
                                  <div className="grid grid-cols-2 gap-2">
                                    <div>
                                      <label className="text-[10px]" style={{ color: '#7C8CA2' }}>Descrizione</label>
                                      <Input value={slot.description || ''} onChange={e => updateSlot(idx, 'description', e.target.value)}
                                        className="text-xs h-7" placeholder="Breve descrizione" />
                                    </div>
                                    <div className="relative">
                                      <label className="text-[10px]" style={{ color: '#7C8CA2' }}>Speaker</label>
                                      <Input value={speakerSearch[idx] ?? slot.speaker ?? ''} 
                                        onChange={e => { searchSpeaker(idx, e.target.value); updateSlot(idx, 'speaker', e.target.value); }}
                                        className="text-xs h-7" placeholder="Cerca speaker..." />
                                      {(speakerResults[idx] || []).length > 0 && (
                                        <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-white border rounded-md shadow-lg max-h-28 overflow-y-auto">
                                          {speakerResults[idx].map((c, ci) => (
                                            <button key={c.id || ci} className="w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50"
                                              onClick={() => {
                                                updateSlot(idx, 'speaker', c.full_name || c.name);
                                                updateSlot(idx, 'speaker_id', c.id);
                                                setSpeakerSearch(p => ({ ...p, [idx]: c.full_name || c.name }));
                                                setSpeakerResults(p => ({ ...p, [idx]: [] }));
                                              }}>
                                              {c.full_name || c.name} {c.company ? `(${c.company})` : ''}
                                            </button>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ))}
                              <button onClick={addAgendaSlot}
                                className="flex items-center gap-1.5 text-xs font-medium text-blue-600 hover:underline"
                                data-testid="add-slot">
                                <Plus className="w-3.5 h-3.5" /> Aggiungi slot
                              </button>
                            </div>
                          </div>
                          <div>
                            <label className="text-[11px] font-medium block mb-2" style={{ color: '#7C8CA2' }}>Materiali</label>
                            <div className="grid grid-cols-2 gap-2">
                              {[
                                { k: 'presentation', l: 'Presentazione' },
                                { k: 'handout', l: 'Handout' },
                                { k: 'recording', l: 'Registrazione video' },
                                { k: 'assessment', l: 'Assessment' },
                              ].map(m => (
                                <label key={m.k} className="flex items-center gap-2 cursor-pointer">
                                  <Checkbox checked={!!materials[m.k]}
                                    onCheckedChange={(v) => setMaterials(prev => ({ ...prev, [m.k]: !!v }))}
                                    data-testid={`material-${m.k}`} />
                                  <span className="text-xs">{m.l}</span>
                                </label>
                              ))}
                            </div>
                          </div>
                        </div>
                      </CollapsibleContent>
                    </Collapsible>

                    {/* OPERATIVO (ex-Logistica) */}
                    <Collapsible defaultOpen={false}>
                      <CollapsibleTrigger asChild>
                        <button className="w-full flex items-center justify-between py-2.5 px-4 text-[12px] font-semibold uppercase tracking-wider hover:bg-gray-50 rounded-t-lg"
                          style={{ color: 'var(--color-primary)' }} data-testid="section-operativo">
                          <span>Operativo</span><ChevronDown className="w-4 h-4" />
                        </button>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <div className="px-4 pb-4 space-y-3 rounded-lg bg-white border" style={{ borderColor: '#E5E7EB' }}>
                          <div className="pt-3 space-y-3">
                            <div>
                              <label className="text-[11px] font-medium block mb-1" style={{ color: '#7C8CA2' }}>Checklist pre-evento</label>
                              <Textarea value={progForm.checklist || ''} onChange={e => setProgForm(p => ({ ...p, checklist: e.target.value }))}
                                placeholder="Cose da preparare..." className="text-xs min-h-[60px]" data-testid="prog-checklist" />
                            </div>
                            <div>
                              <label className="text-[11px] font-medium block mb-1" style={{ color: '#7C8CA2' }}>Link gruppo WhatsApp</label>
                              <Input value={progForm.whatsapp_group_link || ''} onChange={e => setProgForm(p => ({ ...p, whatsapp_group_link: e.target.value }))}
                                placeholder="https://chat.whatsapp.com/..." className="text-xs h-9" data-testid="prog-whatsapp" />
                            </div>
                            <div>
                              <label className="text-[11px] font-medium block mb-1" style={{ color: '#7C8CA2' }}>Note operative</label>
                              <Textarea value={progForm.notes || ''} onChange={e => setProgForm(p => ({ ...p, notes: e.target.value }))}
                                placeholder="Note per il team..." className="text-xs min-h-[60px]" data-testid="prog-notes" />
                            </div>
                            <div>
                              <label className="text-[11px] font-medium block mb-1" style={{ color: '#7C8CA2' }}>Contatti di emergenza</label>
                              <Textarea value={progForm.emergency_contacts || ''} onChange={e => setProgForm(p => ({ ...p, emergency_contacts: e.target.value }))}
                                placeholder="Nome, telefono..." className="text-xs min-h-[60px]" data-testid="prog-emergency" />
                            </div>
                          </div>
                        </div>
                      </CollapsibleContent>
                    </Collapsible>

                    {/* Save button */}
                    <Button onClick={handleSaveProgettazione} disabled={savingProg}
                      className="text-white" style={{ backgroundColor: '#2563EB' }} data-testid="save-progettazione">
                      {savingProg ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
                      Salva evento
                    </Button>
                  </div>
                </TabsContent>
              </div>
            </Tabs>
          </>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center">
            <Calendar size={48} style={{ color: '#D1D5DB' }} className="mb-4" />
            <p className="text-[15px] font-medium" style={{ color: '#4B5563' }}>Seleziona un evento</p>
          </div>
        )}
      </div>

      {/* Cascade Dialog */}
      <Dialog open={showCascadeDialog} onOpenChange={setShowCascadeDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Contenuti collegati all'evento
            </DialogTitle>
          </DialogHeader>
          {cascadeData && (
            <div className="space-y-3 pt-2" data-testid="cascade-dialog">
              <p className="text-sm" style={{ color: '#4B5563' }}>
                Stai modificando {(cascadeData.changes || []).join(', ')} del {selectedEvent?.name || selectedEvent?.title}.
              </p>
              {cascadeData.linked_content?.length > 0 && (
                <div>
                  <p className="text-xs font-medium mb-1">
                    PRE-EVENTO ({cascadeData.linked_content.filter(c => c.is_pre_event).length}):
                  </p>
                  {cascadeData.linked_content.filter(c => c.is_pre_event).map(c => (
                    <div key={c.id} className="text-xs py-1 border-b" style={{ borderColor: '#F3F4F6' }}>
                      {c.title} &mdash; {c.status} &mdash; {c.scheduled_date}
                    </div>
                  ))}
                  <p className="text-xs font-medium mt-2 mb-1">
                    POST-EVENTO ({cascadeData.linked_content.filter(c => !c.is_pre_event).length}):
                  </p>
                  {cascadeData.linked_content.filter(c => !c.is_pre_event).map(c => (
                    <div key={c.id} className="text-xs py-1 border-b" style={{ borderColor: '#F3F4F6' }}>
                      {c.title} &mdash; {c.status} &mdash; {c.scheduled_date}
                    </div>
                  ))}
                </div>
              )}
              <div className="mt-4 space-y-2">
                <Button onClick={() => handleCascade('update_dates')} className="w-full" data-testid="cascade-update-dates">
                  Sposta tutte le date di conseguenza
                </Button>
                <Button onClick={() => handleCascade('delete_pre')} variant="outline" className="w-full" data-testid="cascade-delete-pre">
                  Elimina solo contenuti pre-evento
                </Button>
                <Button onClick={() => handleCascade('delete_all')} variant="destructive" className="w-full" data-testid="cascade-delete-all">
                  Elimina tutti i contenuti collegati
                </Button>
                <Button onClick={() => handleCascade('skip')} variant="ghost" className="w-full" data-testid="cascade-skip">
                  Non toccare i contenuti
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Quick Register Dialog */}
      <Dialog open={showRegForm} onOpenChange={setShowRegForm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-[14px] font-semibold" style={{ color: '#1B2B4B' }}>Registra partecipante</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <input value={regName} onChange={e => setRegName(e.target.value)} placeholder="Nome completo"
              className="w-full rounded-lg px-4 py-2.5 text-[13px] outline-none focus:ring-2 focus:ring-[#2563EB]/20"
              style={{ border: '1px solid #E5E7EB', color: '#1B2B4B' }} data-testid="reg-name-input" />
            <input value={regEmail} onChange={e => setRegEmail(e.target.value)} placeholder="Email"
              className="w-full rounded-lg px-4 py-2.5 text-[13px] outline-none focus:ring-2 focus:ring-[#2563EB]/20"
              style={{ border: '1px solid #E5E7EB', color: '#1B2B4B' }} data-testid="reg-email-input" />
            <button onClick={handleRegister}
              className="w-full py-2.5 rounded-lg text-[13px] font-medium text-white hover:opacity-90 disabled:opacity-50"
              style={{ backgroundColor: '#2563EB' }} disabled={!regName.trim() || !regEmail.trim()}
              data-testid="confirm-register-btn">
              Registra
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Event Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-[16px] font-bold" style={{ color: '#1B2B4B' }}>Nuovo evento</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <div>
              <label className="mb-1 block text-[12px] font-medium" style={{ color: '#1B2B4B' }}>Titolo evento</label>
              <input type="text" value={createForm.title} onChange={e => setCreateForm(f => ({ ...f, title: e.target.value }))}
                placeholder="Es: Workshop AI per PMI ticinesi" className="w-full rounded-lg px-3 py-2 text-[14px]"
                style={{ border: '1px solid #E5E7EB' }} data-testid="event-title-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="mb-1 block text-[12px] font-medium" style={{ color: '#1B2B4B' }}>Tipo</label>
                <select value={createForm.event_type} onChange={e => setCreateForm(f => ({ ...f, event_type: e.target.value }))}
                  className="w-full rounded-lg px-3 py-2 text-[14px]" style={{ border: '1px solid #E5E7EB' }}>
                  <option value="E1">Workshop</option><option value="E2">Webinar</option><option value="E3">Networking</option><option value="E4">Conferenza</option><option value="E5">Masterclass</option>
                </select>
              </div>
              <div>
                <label className="mb-1 block text-[12px] font-medium" style={{ color: '#1B2B4B' }}>Data evento</label>
                <input type="date" value={createForm.event_date} onChange={e => setCreateForm(f => ({ ...f, event_date: e.target.value }))}
                  className="w-full rounded-lg px-3 py-2 text-[14px]" style={{ border: '1px solid #E5E7EB' }} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="mb-1 block text-[12px] font-medium" style={{ color: '#1B2B4B' }}>Luogo</label>
                <input type="text" value={createForm.location} onChange={e => setCreateForm(f => ({ ...f, location: e.target.value }))}
                  placeholder="Es: Lugano, SUPSI" className="w-full rounded-lg px-3 py-2 text-[14px]" style={{ border: '1px solid #E5E7EB' }} />
              </div>
              <div>
                <label className="mb-1 block text-[12px] font-medium" style={{ color: '#1B2B4B' }}>Max partecipanti</label>
                <input type="number" value={createForm.max_participants} onChange={e => setCreateForm(f => ({ ...f, max_participants: parseInt(e.target.value) || 50 }))}
                  className="w-full rounded-lg px-3 py-2 text-[14px]" style={{ border: '1px solid #E5E7EB' }} />
              </div>
            </div>
            <div>
              <label className="mb-1 block text-[12px] font-medium" style={{ color: '#1B2B4B' }}>Descrizione</label>
              <textarea rows={3} value={createForm.description} onChange={e => setCreateForm(f => ({ ...f, description: e.target.value }))}
                placeholder="Breve descrizione dell'evento..." className="w-full rounded-lg px-3 py-2 text-[14px]" style={{ border: '1px solid #E5E7EB' }} />
            </div>
            <button onClick={handleCreateEvent} disabled={!createForm.title.trim()}
              className="w-full py-2.5 rounded-lg text-[14px] font-medium text-white hover:opacity-90 disabled:opacity-50"
              style={{ backgroundColor: '#2563EB' }} data-testid="create-event-submit-btn">
              Crea evento
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Config Dialog */}
      <ConfigDialog open={!!configDialogType} onClose={() => setConfigDialogType(null)}
        configType={configDialogType} onUpdate={loadEvents} />
    </div>
  );
}
